import { computed, unref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useNavigation } from './useNavigation'
import { useAuthStore } from '@/stores/authStore'

export interface HomeCard {
  key: string
  label: string
  description: string
  icon: string
  route: string
}

export interface HomeSection {
  key: string
  label: string
  icon: string
  colorClass: string
  items: HomeCard[]
}

const SECTION_CONFIG: Record<string, { icon: string; colorClass: string }> = {
  ivrManagement: { icon: 'phone_in_talk', colorClass: 'bg-blue-100 text-blue-600' },
  scheduling: { icon: 'calendar_month', colorClass: 'bg-purple-100 text-purple-600' },
  administration: { icon: 'settings', colorClass: 'bg-emerald-100 text-emerald-600' },
}

export function useHomeCards() {
  const { t } = useI18n()
  const { generateMenuItems } = useNavigation()
  const authStore = useAuthStore()

  const greeting = computed(() => {
    const firstName = unref(authStore.currentEmployee)?.firstName
    if (firstName) {
      return t('home.welcomeBack', { name: firstName })
    }
    return t('home.welcome')
  })

  const sections = computed((): HomeSection[] => {
    const menuItems = generateMenuItems()
    const parentSections: HomeSection[] = []
    const standaloneItems: HomeCard[] = []

    for (const item of menuItems) {
      if (item.key === 'home') continue

      if (item.items && item.items.length > 0) {
        const config = SECTION_CONFIG[item.key!] ?? SECTION_CONFIG.administration
        parentSections.push({
          key: item.key!,
          label: item.label as string,
          icon: config.icon,
          colorClass: config.colorClass,
          items: item.items.map(child => ({
            key: child.key!,
            label: child.label as string,
            description: t(`home.cards.${child.key}.description`),
            icon: child.icon!,
            route: child.key!,
          })),
        })
      } else {
        standaloneItems.push({
          key: item.key!,
          label: item.label as string,
          description: t(`home.cards.${item.key}.description`),
          icon: item.icon!,
          route: item.key!,
        })
      }
    }

    if (standaloneItems.length > 0) {
      parentSections.push({
        key: 'administration',
        label: t('home.sections.administration'),
        icon: SECTION_CONFIG.administration.icon,
        colorClass: SECTION_CONFIG.administration.colorClass,
        items: standaloneItems,
      })
    }

    return parentSections
  })

  return {
    greeting,
    sections,
  }
}
